import { useState } from "react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import { Plus, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import AppStoreBadge from "../components/AppStoreBadge";

const QAS = [
  {
    q: "Who is ClientDesk VA for?",
    a: "Virtual assistants, independent service professionals, and small teams that manage clients directly — anyone whose day involves onboarding, scheduling, tasks, documents and invoices for other people.",
  },
  {
    q: "Do I need to install anything?",
    a: "No. ClientDesk VA runs in your browser, and the iPhone and iPad app is available to download from the App Store.",
    appStore: true,
  },
  {
    q: "How does the free trial work?",
    a: "Every new workspace begins on a Starter trial so you can try the core workflow with real data. You can upgrade to Professional or Business from Settings → Billing whenever you're ready.",
  },
  {
    q: "Which calendars can I connect?",
    a: "Google Calendar and Microsoft Outlook, both two-way. Bookings can also generate Google Meet or Microsoft Teams meeting links automatically.",
  },
  {
    q: "How is my client data protected?",
    a: "Requests use TLS 1.2+, passwords are bcrypt-hashed by Supabase Auth, your organization's rows are isolated at the database level with Postgres Row-Level Security, and every mutating action is audit-logged. See the Privacy Policy for the full details.",
  },
  {
    q: "Can I delete my account and data?",
    a: "Yes. From inside the app go to Settings → Account → Delete my account, or email support. See the Account deletion page for the exact timeline of what's removed and what's retained for tax/audit reasons.",
  },
  {
    q: "How do payments work?",
    a: "Web subscriptions and invoice payments run through Stripe on Stripe-hosted pages, so card numbers never touch ClientDesk VA. iOS subscriptions run through Apple's in-app purchase system.",
  },
  {
    q: "Do you offer discounts for annual billing?",
    a: "Not today — pricing is per month, cancel any time. If you need something custom for a larger team, contact us.",
  },
];

function Item({ item, open, onToggle, i }) {
  return (
    <div className="border-b border-[#E8E2D9] py-5">
      <button
        onClick={onToggle}
        data-testid={`faq-item-${i}`}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-6 text-left"
      >
        <span className="font-display font-bold text-[17px] sm:text-[18px] text-[#1A2A3A] tracking-[-0.01em]">
          {item.q}
        </span>
        {open ? (
          <Minus size={18} className="text-[#E8712C] flex-shrink-0" />
        ) : (
          <Plus size={18} className="text-[#57534E] flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="mt-3 text-[15px] text-[#57534E] leading-relaxed max-w-3xl">
          {item.a}
          {item.appStore && (
            <div className="mt-4">
              <AppStoreBadge source="faq" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <>
      <SEO
        title="FAQ"
        description="Frequently asked questions about ClientDesk VA — pricing, trials, integrations, security, data deletion and payments."
        path="/faq"
      />
      <section className="pt-20 pb-8 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#E8712C] mb-3">
              FAQ
            </div>
            <h1 className="font-display font-extrabold text-[36px] sm:text-[52px] leading-[1.05] tracking-[-0.03em] text-[#1A2A3A] mb-5">
              The questions people ask us most.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {QAS.map((item, i) => (
            <Reveal key={item.q} delay={i * 30}>
              <Item
                item={item}
                open={open === i}
                onToggle={() => setOpen(open === i ? -1 : i)}
                i={i}
              />
            </Reveal>
          ))}
          <div className="mt-12 text-center">
            <p className="text-[14px] text-[#57534E]">
              Didn't see your question?{" "}
              <Link to="/contact" className="text-[#E8712C] font-medium hover:underline">
                Contact support
              </Link>
              .
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
